import React, { useMemo } from 'react'
import { csx, Text } from '@vtex/admin-ui'
import { useFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { useIntl, FormattedMessage } from 'react-intl'

import { ProductSkuAvailabilityRow } from './ProductSkuAvailabilityRow'
import {
  Badge,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerTitle,
  useDrawer,
} from '../../../../ui/components'
import { AvailabilityOptionDrawerItem } from './AvailabilityOptionDrawerItem'
import {
  useFormatCurrency,
  useOrderFormShippingEstimate,
} from '../../../Checkout/hooks'
import type { ProductSkuAvailabilityPickup_ProductSkuOfferConnection$key } from './__generated__/ProductSkuAvailabilityPickup_ProductSkuOfferConnection.graphql'
import { DeliveryAvailabilityPrice } from './DeliveryAvailabilityPrice'
import { getBestDeliveryAvailabilities } from './utils'
import { DeliveryChannelIcon, Price } from '../../../../core/components'

const productSkuAvailabilityConnectionFragment = graphql`
  fragment ProductSkuAvailabilityPickup_ProductSkuOfferConnection on ProductSkuOfferConnection {
    edges {
      node {
        deliveryChannel
        netPrice {
          amount
        }
        deliveryAvailabilities {
          edges {
            node {
              name
              pickupDistance
              shippingEstimate
              netPrice {
                amount
              }
            }
          }
        }
      }
    }
  }
`

type PickupAvailabilities = Array<{
  skuOfferNetPrice: number
  node: {
    name: string
    shippingEstimate: string
    pickupDistance: number
    netPrice: {
      amount: number
    }
  }
}>

// [TODO]<internationalization>: KM hardcoded
const getPickupDistanceMessage = (pickupDistance: number) => {
  return pickupDistance ? `${pickupDistance.toFixed(1)} km` : '0 km'
}

export type ProductSkuAvailabilityPickupProps = {
  busy?: boolean
  productSkuOfferConnectionRef: ProductSkuAvailabilityPickup_ProductSkuOfferConnection$key
}

export const ProductSkuAvailabilityPickup = ({
  busy,
  productSkuOfferConnectionRef,
}: ProductSkuAvailabilityPickupProps) => {
  const { getEstimateMessage } = useOrderFormShippingEstimate()
  const formatCurrency = useFormatCurrency()
  const { formatMessage } = useIntl()

  const pickupOptionsDrawer = useDrawer({
    route: '#/availability-pickup-drawer',
  })

  const productSkuOffers = useFragment(
    productSkuAvailabilityConnectionFragment,
    productSkuOfferConnectionRef
  )

  const pickupOffers = useMemo(() => {
    return productSkuOffers.edges.filter(
      (productSkuOffer) =>
        productSkuOffer.node.deliveryChannel === 'PICKUP_IN_POINT'
    )
  }, [productSkuOffers.edges])

  const pickupAvailabilities = useMemo(() => {
    const availabilities = pickupOffers.reduce((acc, currentOffer) => {
      return [
        ...acc,
        ...currentOffer.node.deliveryAvailabilities.edges.map(
          (deliveryAvailability) => ({
            ...deliveryAvailability,
            skuOfferNetPrice: currentOffer.node.netPrice.amount / 100,
          })
        ),
      ]
    }, [] as PickupAvailabilities)

    const { cheapest, fastest } = getBestDeliveryAvailabilities(availabilities)

    return availabilities
      .map((availability) => ({
        ...availability,
        isCheapest: cheapest.node.name === availability.node.name,
        isFastest: fastest.node.name === availability.node.name,
      }))
      .sort((a) => (a.isCheapest || a.isFastest ? -1 : 0))
  }, [pickupOffers])

  const renderProductSkuAvailabilityMessage = () => {
    const fastestPickup = pickupAvailabilities.find(
      ({ isFastest }) => isFastest
    )

    if (!fastestPickup) {
      return formatMessage({ id: 'unavailable' })
    }

    const fastestShippingEstimate = getEstimateMessage(
      fastestPickup.node.shippingEstimate
    ).toLowerCase()

    const fastestPrice = fastestPickup.node.netPrice.amount ? (
      formatCurrency(fastestPickup.node.netPrice.amount / 100)
    ) : (
      <Text variant="action1" tone="positive">
        {formatMessage({ id: 'freeShipping' })}
      </Text>
    )

    return (
      <>
        {fastestPrice}
        {` ${fastestShippingEstimate}`}
      </>
    )
  }

  const handleSlaBadgeClick = () => {
    pickupOptionsDrawer.open()
  }

  const handleDrawerClose = () => {
    pickupOptionsDrawer.close()
  }

  const renderRightColumn = () => {
    if (!pickupAvailabilities.length) return null

    return (
      <Badge onClick={handleSlaBadgeClick}>
        <FormattedMessage
          id="pdp.sku-availability.options-quantity"
          values={{
            optionsQuantity: pickupAvailabilities.length,
          }}
        />
      </Badge>
    )
  }

  return (
    <>
      <ProductSkuAvailabilityRow
        icon={<DeliveryChannelIcon deliveryChannel="PICKUP_IN_POINT" />}
        title={formatMessage({ id: 'pdp.sku-availability.pickup.title' })}
        message={renderProductSkuAvailabilityMessage()}
        status={pickupAvailabilities.length > 0 ? 'available' : 'unavailable'}
        rightColumn={renderRightColumn()}
        busy={busy}
      />
      <Drawer isOpen={pickupOptionsDrawer.isOpen} onClose={handleDrawerClose}>
        <DrawerCloseButton />
        <DrawerTitle>
          <FormattedMessage id="pdp.sku-availability.drawer-pickup.title" />
        </DrawerTitle>
        <DrawerBody>
          {pickupAvailabilities.map((pickupAvailability) => (
            <AvailabilityOptionDrawerItem
              key={
                pickupAvailability.node.name +
                pickupAvailability.node.shippingEstimate
              }
              icon={
                <>
                  <DeliveryChannelIcon
                    deliveryChannel="PICKUP_IN_POINT"
                    className={csx({
                      color: '$gray50',
                      marginRight: '$space-2',
                    })}
                  />
                  <Text variant="detail" tone="secondary">
                    <Price value={pickupAvailability.skuOfferNetPrice} />
                  </Text>
                </>
              }
              title={
                <Text variant="action1">{pickupAvailability.node.name}</Text>
              }
              description={
                <Text variant="action2" tone="secondary">
                  {getEstimateMessage(pickupAvailability.node.shippingEstimate)}
                </Text>
              }
              price={
                <DeliveryAvailabilityPrice
                  price={pickupAvailability.node.netPrice.amount}
                />
              }
              info={
                <Text variant="detail" tone="secondary">
                  {getPickupDistanceMessage(
                    pickupAvailability.node.pickupDistance
                  )}
                </Text>
              }
            />
          ))}
        </DrawerBody>
      </Drawer>
    </>
  )
}
